/**
 * Subscription Context
 * 
 * Provides global subscription state management for the ArbiWin platform.
 * Manages subscription status, features, and related operations.
 */

import React, { createContext } from 'react';
import { SUBSCRIPTION_STATUS, SUBSCRIPTION_TYPES } from '../constants/subscriptionTypes';

/**
 * Default subscription state
 */
export const DEFAULT_SUBSCRIPTION_STATE = {
  // Subscription status
  status: SUBSCRIPTION_STATUS.LOADING,
  
  // Subscription type
  type: null,
  
  // Subscription ID
  id: null,
  
  // User ID
  userId: null,
  
  // User email
  email: null,
  
  // Activation date
  activatedAt: null,
  
  // Expiry date
  expiresAt: null,
  
  // Renewal date
  renewsAt: null,
  
  // Features available
  features: [],

  // Stripe tier from /subscriptions/status (PREMIUM when the field is absent)
  tier: 'PREMIUM',

  // Ultimate near-arb tab; false when the backend omits the flag
  nearArbEnabled: false,
  
  // Loading states
  loading: {
    status: false,
    activation: false,
    deactivation: false,
    renewal: false
  },
  
  // Error states
  error: null,
  
  // Last updated timestamp
  lastUpdated: null,
  
  // Cache data
  cache: {
    data: null,
    timestamp: null,
    isValid: false
  }
};

/**
 * Subscription Context
 */
export const SubscriptionContext = createContext({
  // State
  ...DEFAULT_SUBSCRIPTION_STATE,
  
  // Actions
  actions: {
    // Check subscription status
    checkStatus: () => {},
    
    // Activate subscription
    activate: () => {},
    
    // Deactivate subscription
    deactivate: () => {},
    
    // Renew subscription
    renew: () => {},
    
    // Cancel subscription
    cancel: () => {},
    
    // Refresh subscription data
    refresh: () => {},
    
    // Clear subscription data
    clear: () => {},
    
    // Set loading state
    setLoading: () => {},
    
    // Set error state
    setError: () => {},
    
    // Clear error
    clearError: () => {}
  },
  
  // Computed properties
  computed: {
    // Check if subscription is active
    isActive: false,
    
    // Check if subscription is expired
    isExpired: false,
    
    // Check if subscription is inactive
    isInactive: false,
    
    // Check if subscription has specific feature
    hasFeature: () => false,
    
    // Get days until expiry
    daysUntilExpiry: null,
    
    // Get subscription status label
    statusLabel: '',
    
    // Get subscription type label
    typeLabel: '',
    
    // Check if subscription needs renewal
    needsRenewal: false,
    
    // Check if subscription is in grace period
    isInGracePeriod: false,

    // Near-arb tab is visible only when the backend enables it for this user
    nearArbEnabled: false,

    tier: 'PREMIUM',
  }
});

/**
 * Subscription Context Provider Props
 */
export const SubscriptionProvider = SubscriptionContext.Provider;

/**
 * Subscription Context Consumer
 */
export const SubscriptionConsumer = SubscriptionContext.Consumer;
